import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Activity, Package, BarChart3, FileText, Upload, ShoppingCart, Users, Settings, Wallet, ClipboardList, Truck, Building2, ClipboardCheck, Link2, PieChart, TrendingDown, TrendingUp, FileBarChart, ChevronDown, Briefcase, ClipboardX, Cog, Printer, Calendar, DollarSign, Calculator, PiggyBank, Handshake, UserCog, Tag, Tags, Trophy, Image, LayoutDashboard, Receipt, Shield, ShieldCheck, ShieldAlert, LogOut, User, History, Camera, Percent, Brain, RefreshCw, Sparkles, Search, Database, MailCheck, Target, ArrowUpCircle, ArrowDownCircle, Edit2, X, StickyNote, Menu, Clock, Scale, Boxes, Smartphone, Banknote, Megaphone, Box, ShoppingBag, Landmark, MapPin, Scissors, Gift, Globe } from "lucide-react";
import { OfflineIndicator } from "./OfflineIndicator";
import { ManagerAlertListener } from "./ManagerAlertListener";
import SastClock from "./SastClock";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import axios from "axios";
import { initQzTray, getDefaultPrinter } from "@/utils/qzPrint";

const API = process.env.REACT_APP_BACKEND_URL;

// Daily Sales Target Tracker Component
const DailySalesTarget = () => {
  const [target, setTarget] = useState(() => {
    const saved = localStorage.getItem('dailySalesTarget');
    const savedDate = localStorage.getItem('dailySalesTargetDate');
    const today = new Date().toISOString().split('T')[0];
    // Reset target if it's a new day
    if (savedDate !== today) {
      return 0;
    }
    return saved ? parseFloat(saved) : 0;
  });
  const [actualSales, setActualSales] = useState(0);
  const [breakdown, setBreakdown] = useState({ sales_only_total: 0, account_receipts_total: 0 });
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isHidden, setIsHidden] = useState(() => {
    return localStorage.getItem('dailySalesTargetHidden') === 'true';
  });

  // Fetch actual sales for today - uses lightweight aggregation endpoint
  const fetchTodaySales = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API}/api/sales/daily-total-quick`, {
        // Cache-buster so stale ingress/CDN responses can never stick
        params: { date: today, _t: Date.now() },
        timeout: 8000
      });
      const totalSales = response.data?.sales_total || 0;
      setActualSales(totalSales);
      setBreakdown({
        sales_only_total: response.data?.sales_only_total || 0,
        account_receipts_total: response.data?.account_receipts_total || 0,
      });
    } catch (error) {
      console.error("Error fetching today's sales:", error);
    }
  };

  useEffect(() => {
    fetchTodaySales();
    // Refresh every 60 seconds (reduced from 30s to ease DB load)
    const interval = setInterval(fetchTodaySales, 60000);
    return () => clearInterval(interval);
  }, []);

  const saveTarget = () => {
    const value = parseFloat(inputValue) || 0;
    setTarget(value);
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('dailySalesTarget', value.toString());
    localStorage.setItem('dailySalesTargetDate', today);
    setEditing(false);
  };

  const toggleHide = (e) => {
    e.stopPropagation();
    const newHidden = !isHidden;
    setIsHidden(newHidden);
    localStorage.setItem('dailySalesTargetHidden', newHidden.toString());
  };

  const difference = actualSales - target;
  const percentAchieved = target > 0 ? (actualSales / target * 100) : 0;

  const formatR = (val) => `R ${Math.abs(val).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Show minimized version when hidden
  if (isHidden) {
    return (
      <button
        onClick={toggleHide}
        className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 rounded-lg px-2 py-1 text-xs text-gray-500 transition-colors"
        title="Show daily sales target"
      >
        <Target className="h-4 w-4" />
        <span className="text-xs">Show Target</span>
      </button>
    );
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1">
        <Target className="h-4 w-4 text-purple-600" />
        <Input
          type="number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Target..."
          className="h-6 w-24 text-xs"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveTarget();
            if (e.key === 'Escape') setEditing(false);
          }}
        />
        <Button size="sm" className="h-6 px-2 text-xs" onClick={saveTarget}>Set</Button>
      </div>
    );
  }

  if (target === 0) {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={() => { setInputValue(''); setEditing(true); }}
          className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 rounded-lg px-2 py-1 text-xs text-gray-600 transition-colors"
          title="Set daily sales target"
        >
          <Target className="h-4 w-4" />
          Set Target
        </button>
        <button
          onClick={toggleHide}
          className="text-gray-400 hover:text-gray-600 p-1"
          title="Hide target tracker"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <div 
        className={`flex items-center gap-1 rounded-lg px-2 py-0.5 text-[11px] font-medium cursor-pointer transition-colors ${
          difference >= 0 
            ? 'bg-green-50 text-green-700 hover:bg-green-100' 
            : 'bg-red-50 text-red-700 hover:bg-red-100'
        }`}
        onClick={() => { setInputValue(target.toString()); setEditing(true); }}
        title={`Target: ${formatR(target)} | Sales: ${formatR(breakdown.sales_only_total)} | Account Receipts: ${formatR(breakdown.account_receipts_total)} | Click to edit`}
        data-testid="daily-target-widget"
      >
        <Target className="h-3.5 w-3.5" />
        <span className="hidden xl:inline">{formatR(target)}</span>
        <span className="hidden xl:inline text-gray-500">/</span>
        <span>{formatR(actualSales)}</span>
        {difference >= 0 ? (
          <span className="flex items-center gap-0.5 text-green-600 font-bold">
            <ArrowUpCircle className="h-3 w-3" />
            +{formatR(difference)}
          </span>
        ) : (
          <span className="flex items-center gap-0.5 text-red-600 font-bold" title="Amount still needed to reach the daily target">
            <ArrowDownCircle className="h-3 w-3" />
            {formatR(difference)} to go
          </span>
        )}
        {target > 0 && (
          <span className={`${percentAchieved >= 100 ? 'text-green-600 font-bold' : 'text-gray-500'}`}>
            ({percentAchieved.toFixed(0)}%)
          </span>
        )}
        <Edit2 className="h-3 w-3 text-gray-400" />
      </div>
      <button
        onClick={toggleHide}
        className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
        title="Hide target tracker"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// Monthly Sales Target Tracker — mirrors the daily widget but for the
// running calendar month. Target value resets when a new month begins.
const MonthlySalesTarget = () => {
  const currentMonthKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const [target, setTarget] = useState(() => {
    const saved = localStorage.getItem('monthlySalesTarget');
    const savedMonth = localStorage.getItem('monthlySalesTargetMonth');
    if (savedMonth !== currentMonthKey()) return 0;
    return saved ? parseFloat(saved) : 0;
  });
  const [actualSales, setActualSales] = useState(0);
  const [breakdown, setBreakdown] = useState({ sales_only_total: 0, account_receipts_total: 0 });
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isHidden, setIsHidden] = useState(() => {
    return localStorage.getItem('monthlySalesTargetHidden') === 'true';
  });

  const fetchMonthTotal = async () => {
    try {
      const response = await axios.get(`${API}/api/sales/monthly-total-quick`, {
        params: { _t: Date.now() },
        timeout: 8000,
      });
      // Track pure sales turnover only — matches the Financial Summary
      // headline figure. Account-payment receipts (customers settling old
      // tabs) are shown separately in the tooltip so the owner can still
      // see total cash-in for the month.
      setActualSales(response.data?.sales_only_total || 0);
      setBreakdown({
        sales_only_total: response.data?.sales_only_total || 0,
        account_receipts_total: response.data?.account_receipts_total || 0,
      });
    } catch (error) {
      console.error("Error fetching monthly sales:", error);
    }
  };

  useEffect(() => {
    fetchMonthTotal();
    // Monthly figure changes slowly — poll every 5 minutes (much less
    // pressure on the DB than the per-minute daily widget).
    const interval = setInterval(fetchMonthTotal, 300000);
    return () => clearInterval(interval);
  }, []);

  const saveTarget = () => {
    const value = parseFloat(inputValue) || 0;
    setTarget(value);
    localStorage.setItem('monthlySalesTarget', value.toString());
    localStorage.setItem('monthlySalesTargetMonth', currentMonthKey());
    setEditing(false);
  };

  const toggleHide = (e) => {
    e.stopPropagation();
    const newHidden = !isHidden;
    setIsHidden(newHidden);
    localStorage.setItem('monthlySalesTargetHidden', newHidden.toString());
  };

  const difference = actualSales - target;
  const percentAchieved = target > 0 ? (actualSales / target * 100) : 0;
  const formatR = (val) => `R ${Math.abs(val).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const monthLabel = new Date().toLocaleString('en-ZA', { month: 'short' }).toUpperCase();

  if (isHidden) {
    return (
      <button
        onClick={toggleHide}
        className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 rounded-lg px-2 py-1 text-xs text-gray-500 transition-colors"
        title="Show monthly sales target"
        data-testid="monthly-target-toggle"
      >
        <Calendar className="h-4 w-4" />
        <span className="text-xs">Show {monthLabel}</span>
      </button>
    );
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1 bg-indigo-50 rounded-lg px-2 py-1">
        <Calendar className="h-4 w-4 text-indigo-600" />
        <span className="text-[10px] font-semibold text-indigo-700">{monthLabel}</span>
        <Input
          type="number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Month target..."
          className="h-6 w-28 text-xs"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveTarget();
            if (e.key === 'Escape') setEditing(false);
          }}
          data-testid="monthly-target-input"
        />
        <Button size="sm" className="h-6 px-2 text-xs" onClick={saveTarget} data-testid="monthly-target-save">Set</Button>
      </div>
    );
  }

  if (target === 0) {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={() => { setInputValue(''); setEditing(true); }}
          className="flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 rounded-lg px-2 py-1 text-xs text-indigo-700 transition-colors"
          title={`Set monthly sales target for ${monthLabel}`}
          data-testid="set-monthly-target-btn"
        >
          <Calendar className="h-4 w-4" />
          Set {monthLabel}
        </button>
        <button
          onClick={toggleHide}
          className="text-gray-400 hover:text-gray-600 p-1"
          title="Hide monthly target tracker"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <div
        className={`flex items-center gap-1 rounded-lg px-2 py-0.5 text-[11px] font-medium cursor-pointer transition-colors ${
          difference >= 0
            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
        }`}
        onClick={() => { setInputValue(target.toString()); setEditing(true); }}
        title={`Sales turnover (${monthLabel}) — matches Financial Summary\nTarget: ${formatR(target)}\nMTD Sales: ${formatR(breakdown.sales_only_total)}\nAccount Receipts (settling old tabs, shown separately): ${formatR(breakdown.account_receipts_total)}\nTotal cash in: ${formatR(breakdown.sales_only_total + breakdown.account_receipts_total)}\nClick to edit target`}
        data-testid="monthly-target-widget"
      >
        <Calendar className="h-3.5 w-3.5" />
        <span className="text-[10px] font-semibold opacity-80">{monthLabel}</span>
        <span className="hidden xl:inline">{formatR(target)}</span>
        <span className="hidden xl:inline text-gray-500">/</span>
        <span>{formatR(actualSales)}</span>
        {difference >= 0 ? (
          <span className="flex items-center gap-0.5 text-emerald-700 font-bold">
            <ArrowUpCircle className="h-3 w-3" />
            +{formatR(difference)}
          </span>
        ) : (
          <span className="flex items-center gap-0.5 text-orange-700 font-bold" title="Amount still needed to reach the monthly target">
            <ArrowDownCircle className="h-3 w-3" />
            {formatR(difference)} to go
          </span>
        )}
        {target > 0 && (
          <span className={`${percentAchieved >= 100 ? 'text-emerald-700 font-bold' : 'text-gray-500'}`}>
            ({percentAchieved.toFixed(0)}%)
          </span>
        )}
        <Edit2 className="h-3 w-3 text-gray-400" />
      </div>
      <button
        onClick={toggleHide}
        className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
        title="Hide monthly target tracker"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// Initialize QZ Tray connection once when app loads
// This prevents multiple "Allow" prompts by using singleton pattern
let qzInitPromise = null;
const initializeQzOnce = async () => {
  // Already initialized or in progress - skip
  if (qzInitPromise) return qzInitPromise;
  
  const savedPrinter = getDefaultPrinter();
  if (savedPrinter) {
    qzInitPromise = initQzTray().then(() => {
      console.log('QZ Tray pre-initialized for silent printing');
    }).catch((e) => {
      console.log('QZ Tray not available:', e.message);
      qzInitPromise = null; // Allow retry if failed
    });
  }
  return qzInitPromise;
};

// Retail Outlook Indicator Component
const OutlookIndicator = () => {
  const [direction, setDirection] = useState(null);
  const [reason, setReason] = useState("");
  const [date, setDate] = useState("");

  useEffect(() => {
    const fetchOutlook = async () => {
      try {
        const response = await axios.get(`${API}/api/outlook/indicator`);
        setDirection(response.data.direction);
        setReason(response.data.reason || "");
        setDate(response.data.date || "");
      } catch (error) {
        console.error("Error fetching outlook:", error);
      }
    };
    fetchOutlook();
    // Refresh every hour
    const interval = setInterval(fetchOutlook, 3600000);
    return () => clearInterval(interval);
  }, []);

  if (!direction) return null;

  const isUp = direction === "up";
  // Friendly fallback if older records have no reason stored yet.
  const tooltipReason = reason ||
    "Daily AI-driven snapshot of SA retail outlook for hardware / building-supply stores. Re-evaluates every morning.";

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 cursor-help focus:outline-none"
            data-testid="sa-retail-indicator"
            aria-label={`SA Retail outlook is ${direction}`}
          >
            <span className="text-xs text-gray-500 font-medium">SA RETAIL</span>
            {isUp ? (
              <TrendingUp className="h-6 w-6 text-green-500" strokeWidth={2.5} />
            ) : (
              <TrendingDown className="h-6 w-6 text-red-500" strokeWidth={2.5} />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className="max-w-xs bg-gray-900 text-white text-xs leading-relaxed px-3 py-2"
          data-testid="sa-retail-tooltip"
        >
          <div className={`font-semibold mb-1 ${isUp ? "text-green-300" : "text-red-300"}`}>
            Outlook: {isUp ? "Positive" : "Negative"}
            {date ? ` · ${date}` : ""}
          </div>
          <div className="text-gray-100">{tooltipReason}</div>
          <div className="text-[10px] text-gray-400 mt-1.5 border-t border-gray-700 pt-1">
            AI-generated daily — for guidance only.
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const [managementOpen, setManagementOpen] = useState(false);
  // Track which management group is currently expanded inside the dropdown.
  // Persists across navigation via localStorage so the menu remembers what
  // the user was last looking at.
  const [expandedGroup, setExpandedGroup] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem("management_expanded_group") || null;
  });
  const toggleGroup = (title) => {
    setExpandedGroup((cur) => {
      const next = cur === title ? null : title;
      try { window.localStorage.setItem("management_expanded_group", next || ""); } catch (_) { /* noop */ }
      return next;
    });
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Compact mode: on the till screen the secondary "Tools" row is hidden by
  // default so the sidebar fits on small monitors. The cashier can pop it
  // open with the ⌃ Tools button — preference is remembered per session.
  const isTillScreen = location.pathname === "/sales";
  const [secondaryNavOpen, setSecondaryNavOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    const saved = window.sessionStorage.getItem("pos_secondary_nav_open");
    if (saved !== null) return saved === "true";
    return !(window.location.pathname === "/sales");
  });
  useEffect(() => {
    try { window.sessionStorage.setItem("pos_secondary_nav_open", String(secondaryNavOpen)); } catch (_) { /* noop */ }
    // Tell the till page exactly how much vertical space the chrome is using
    // so the right-hand action sidebar can grow when Tools is collapsed.
    if (typeof document !== "undefined") {
      const h = secondaryNavOpen ? "96px" : "48px";
      document.documentElement.style.setProperty("--nav-chrome-h", h);
    }
  }, [secondaryNavOpen]);
  
  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
    setManagementOpen(false);
  }, [location.pathname]);
  
  // Initialize QZ Tray once when Layout mounts (user is logged in)
  useEffect(() => {
    initializeQzOnce();
  }, []);

  // Fetch the warehouse module toggle so the sidebar can gate its menu entry.
  // The endpoint is cheap and falls back gracefully when the API is offline.
  const [warehouseEnabled, setWarehouseEnabled] = useState(false);

  // Company logo for the navbar — falls back to the generic Package icon
  // when no logo is configured.
  const [companyLogoUrl, setCompanyLogoUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/settings/company`);
        if (!cancelled) setCompanyLogoUrl(res.data?.logo_url || "");
      } catch (_e) { /* leave blank → Package icon fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/locations/module/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setWarehouseEnabled(!!data.enabled);
      } catch (_) { /* offline / first boot — assume off */ }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setManagementOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cashier can only see these
  const cashierNavItems = [
    { path: "/", label: "Sales", icon: ShoppingCart },
    { path: "/staff-clock", label: "Staff Clock-In", icon: Clock },
    { path: "/cashup", label: "Cash-Up", icon: Calculator },
    { path: "/online-orders", label: "Online Orders", icon: Globe },
    { path: "/print-stocktake", label: "Print Stocktake", icon: Printer },
  ];
  
  // Admin primary nav items (first row)
  const primaryNavItems = [
    { path: "/smart-dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/", label: "Sales", icon: ShoppingCart },
    { path: "/quotation-management", label: "Quotes", icon: FileText },
    { path: "/stock-orders", label: "Stock Orders", icon: ClipboardCheck },
    { path: "/grv", label: "GRV", icon: Truck },
    { path: "/bin-assign", label: "Bin Assign", icon: MapPin },
    { path: "/stock-count", label: "Stock Count", icon: ClipboardCheck },
    { path: "/stock-master", label: "Stock Master", icon: Package },
    { path: "/stock-conversions", label: "Stock Conversions", icon: Scissors },
    { path: "/customers", label: "Customers", icon: Users },
    { path: "/suppliers", label: "Suppliers", icon: Building2 },
    { path: "/print-stocktake", label: "Print Stocktake", icon: Printer },
  ];
  
  // Secondary nav items (second row)
  const secondaryNavItems = [
    { path: "/notes", label: "Notes", icon: StickyNote },
    { path: "/document-lookup", label: "Document Lookup", icon: Search },
    { path: "/product-assistant", label: "Product Assistant", icon: Sparkles },
    { path: "/ai-features", label: "AI Features", icon: Brain },
    { path: "/agrinet-sync", label: "Agrinet Sync", icon: RefreshCw },
    { path: "/promotions", label: "Promotions", icon: Tag },
    { path: "/gift-vouchers", label: "Gift Vouchers", icon: Gift },
    { path: "/online-orders", label: "Online Orders", icon: Globe },
    { path: "/store-notice", label: "Store Notice", icon: Megaphone },
    { path: "/product-images", label: "Product Images", icon: Camera },
    { path: "/supplier-allocation", label: "Sales Allocation", icon: Link2 },
    { path: "/supplier-order-list", label: "Order List", icon: ClipboardList },
    { path: "/makro-direct", label: "Makro Direct", icon: ShoppingBag },
    { path: "/reorder", label: "Reorder", icon: FileText },
    { path: "/negative-stock", label: "Negative Stock", icon: TrendingDown },
    { path: "/expiry", label: "Expiry", icon: Calendar },
    { path: "/import", label: "Import", icon: Upload },
    { path: "/live-monitor", label: "Live POS Monitor", icon: Activity },
    { path: "/internal-splits", label: "Internal Split", icon: Scale },
    { path: "/print-station", label: "Print Station", icon: Printer },
    { path: "/internal-supplies", label: "Internal Supplies", icon: Box },
  ];
  
  const managementGroups = [
    {
      title: "HR & PAYROLL",
      groupIcon: UserCog,
      items: [
        { path: "/employees", label: "Employees", icon: UserCog },
        { path: "/payroll-runs", label: "Payroll Runs", icon: Wallet },
        { path: "/casual-wages", label: "Casual Wages", icon: Banknote },
        { path: "/staff-clock", label: "Staff Clock-In", icon: Clock },
        { path: "/users", label: "User Management", icon: Users },
      ]
    },
    {
      title: "DAILY OPERATIONS",
      groupIcon: Receipt,
      items: [
        { path: "/daily-totals", label: "Daily Totals", icon: Receipt },
        { path: "/cashup", label: "Cash-Up", icon: Calculator },
        { path: "/flash-reconciliation", label: "Flash Reconciliation", icon: Smartphone },
        { path: "/airtime-machine", label: "Airtime Machine", icon: Smartphone },
        { path: "/petty-cash", label: "Petty Cash", icon: Wallet },
      ]
    },
    {
      title: "REPORTS & ANALYTICS",
      groupIcon: BarChart3,
      items: [
        { path: "/income-statement", label: "Income Statement", icon: FileText },
        { path: "/financial-summary", label: "Monthly Financials", icon: PieChart },
        { path: "/yearly-summary", label: "Yearly Summary", icon: Calendar },
        { path: "/cash-flow-planner", label: "Cash Flow Planner", icon: Landmark },
        { path: "/audit-pack", label: "Audit Pack", icon: FileText },
        { path: "/balance-sheet", label: "Balance Sheet", icon: Scale },
        { path: "/smart-procurement", label: "Smart Procurement", icon: Sparkles },
        { path: "/vat-report", label: "VAT Report (SARS)", icon: Calculator },
        { path: "/sales-audit", label: "Sales Audit", icon: ShieldCheck },
        { path: "/price-changes", label: "Price Audit", icon: ShieldAlert },
        { path: "/accountant-pack", label: "Accountant Pack", icon: Briefcase },
        { path: "/buying-group-report", label: "Buying Group Report", icon: Handshake },
        { path: "/reports", label: "Reports", icon: FileBarChart },
        { path: "/commission", label: "Commission Engine", icon: Trophy },
        { path: "/self-use-audit", label: "Self-Use Audit", icon: Package },
      ]
    },
    {
      title: "PRICING & STOCK",
      groupIcon: Tags,
      items: [
        { path: "/markup-coverage", label: "Markup Calculator", icon: Calculator },
        { path: "/price-suggestions", label: "Price Suggestions", icon: Tags },
        { path: "/unit-price", label: "Unit Price Comparator", icon: Calculator },
        { path: "/raise-affordability", label: "Raise Affordability", icon: Wallet },
        { path: "/stock-labels", label: "Stock Labels / Shelf Talkers", icon: Tag },
        { path: "/flyer-rebrander", label: "Flyer Rebrander", icon: Image },
        { path: "/stock-take", label: "Stock Take", icon: ClipboardX },
        { path: "/consignment", label: "Consignment Stock", icon: Handshake },
      ]
    },
    {
      title: "ACCOUNTS & DOCUMENTS",
      groupIcon: Briefcase,
      items: [
        { path: "/supplier-documents", label: "Supplier Documents", icon: Briefcase },
        { path: "/asset-manager", label: "Asset Manager", icon: Boxes },
        { path: "/settings/account-discounts", label: "Account Discounts", icon: Percent },
        { path: "/settings/customer-levels", label: "Customer Levels", icon: Percent },
      ]
    },
    {
      title: "SYSTEM & SETTINGS",
      groupIcon: Cog,
      items: [
        { path: "/settings", label: "Company Settings", icon: Cog },
        { path: "/settings/security", label: "Security Settings", icon: Shield },
        { path: "/backup", label: "Backup & Protect", icon: Shield },
        { path: "/audit-log", label: "Audit Log", icon: History },
        { path: "/data-integrity", label: "Data Integrity", icon: Database },
        { path: "/data-quality", label: "Data Quality Check", icon: Database },
        { path: "/customer-email-preferences", label: "Email Preferences", icon: MailCheck },
        { path: "/printer-settings", label: "Printer Setup", icon: Printer },
        { path: "/warehouse/locations", label: "Warehouse & Locations", icon: Boxes },
      ]
    },
  ];
  
  // Flatten for active check
  const managementItems = managementGroups.flatMap(group => group.items);
  
  const isManagementActive = managementItems.some(item => location.pathname === item.path);
  
  const NavLink = ({ item }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md whitespace-nowrap ${
          isActive
            ? "bg-purple-100 text-purple-700"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        }`}
      >
        <Icon className="h-3.5 w-3.5 mr-1" />
        {item.label}
      </Link>
    );
  };
  
  // Cashier simplified layout
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="max-w-full mx-auto px-3">
            <div className="flex justify-between items-center h-12">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="flex items-center flex-shrink-0">
                  <Package className="h-7 w-7 text-purple-600" />
                  <span className="ml-2 text-lg font-bold text-gray-900">POS</span>
                </div>
                
                <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
                  {cashierNavItems.map((item) => (
                    <NavLink key={item.path} item={item} />
                  ))}
                </div>
              </div>
              
              <div className="flex items-center gap-2 flex-shrink-0">
                <SastClock />
                <OfflineIndicator />
                
                <div className="flex items-center gap-2 ml-2 pl-2 border-l">
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <User className="h-3 w-3" />
                    <span className="font-medium">{user?.display_name}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                    className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-3 w-3 mr-1" />
                    Logout
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </nav>
        <main className="max-w-full mx-auto">
          {children}
        </main>
      </div>
    );
  }
  
  // Admin full layout
  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-full mx-auto px-3">
          {/* First Row - Logo, Primary Nav (desktop), DailySalesTarget, Management, Offline, User.
              Uses min-h-12 (not h-12) + py-1 so primary-nav items can wrap onto a
              second visual row when the window is narrow, instead of horizontally
              scrolling. The right-hand controls (clock, user, logout) stay
              top-aligned via items-start. */}
          <div className="flex justify-between items-start min-h-12 py-1 gap-2 border-b border-gray-100">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1">
              <div className="flex items-center flex-shrink-0">
                {companyLogoUrl ? (
                  <img
                    src={companyLogoUrl}
                    alt="Company logo"
                    className="h-8 w-auto max-w-[120px] object-contain"
                    data-testid="navbar-company-logo"
                  />
                ) : (
                  <>
                    <Package className="h-7 w-7 text-purple-600" />
                    <span className="ml-2 text-lg font-bold text-gray-900">POS</span>
                  </>
                )}
              </div>
              
              {/* Primary nav - visible on desktop only. Uses flex-wrap so
                  every tab stays visible (no horizontal scroll) even on
                  narrower office screens. */}
              <div className="hidden lg:flex flex-wrap items-center gap-x-1 gap-y-1 min-w-0">
                {primaryNavItems.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </div>
              
              {/* Mobile: show key nav items */}
              <div className="flex lg:hidden flex-wrap items-center gap-x-1 gap-y-1 min-w-0">
                {primaryNavItems.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Daily Sales Target — shown here only when the Tools row is
                  collapsed (e.g. on the till) so the cashier always sees
                  today's progress. When Tools is open, the widget lives at
                  the end of the Tools row to fill the empty space next to
                  the wrapping nav items. */}
              {!secondaryNavOpen && (
                <div className="hidden lg:flex flex-shrink-0 items-center gap-2">
                  <DailySalesTarget />
                  <MonthlySalesTarget />
                </div>
              )}

              {/* Mobile hamburger menu - visible on small screens only */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`lg:hidden inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md ${
                  mobileMenuOpen ? "bg-purple-100 text-purple-700" : "text-gray-600 hover:bg-gray-100"
                }`}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                <span className="ml-1">{mobileMenuOpen ? 'Close' : 'More'}</span>
              </button>

              {/* Toggle for the secondary Tools nav — defaults to closed on
                  the till screen so the sidebar fits on small monitors. */}
              <button
                type="button"
                onClick={() => setSecondaryNavOpen((v) => !v)}
                className={`hidden lg:inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
                  secondaryNavOpen
                    ? "bg-purple-100 text-purple-700"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
                title={secondaryNavOpen ? "Hide tools row" : "Show tools row"}
                data-testid="toggle-secondary-nav"
              >
                <ChevronDown className={`h-3 w-3 mr-1 transition-transform ${secondaryNavOpen ? "" : "-rotate-90"}`} />
                Tools
              </button>

              {/* Management Dropdown - desktop only */}
              <div className="relative hidden lg:block" ref={dropdownRef}>
                <button
                  onClick={() => setManagementOpen(!managementOpen)}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
                    isManagementActive
                      ? "bg-purple-100 text-purple-700"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  <Briefcase className="h-3.5 w-3.5 mr-1" />
                  Management
                  <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${managementOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {managementOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border z-50 max-h-[80vh] overflow-y-auto">
                    {managementGroups.map((group, groupIndex) => {
                      const GroupIcon = group.groupIcon || Briefcase;
                      const activeInGroup = group.items.some(it => location.pathname === it.path);
                      const isExpanded = expandedGroup === group.title || activeInGroup;
                      return (
                        <div key={group.title}>
                          {groupIndex > 0 && <div className="border-t my-1"></div>}
                          <button
                            type="button"
                            onClick={() => toggleGroup(group.title)}
                            className={`w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                              activeInGroup ? "bg-purple-50 text-purple-700" : "text-gray-600 bg-gray-50 hover:bg-gray-100"
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <GroupIcon className="h-4 w-4" />
                              {group.title}
                              <span className="text-[10px] font-normal text-gray-400 normal-case">({group.items.length})</span>
                            </span>
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                          </button>
                          {isExpanded && (
                            <div className="py-1 bg-white">
                              {group.items.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.path;
                                return (
                                  <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setManagementOpen(false)}
                                    className={`flex items-center px-5 py-2 text-sm ${
                                      isActive
                                        ? "bg-purple-50 text-purple-700"
                                        : "text-gray-700 hover:bg-gray-100"
                                    }`}
                                  >
                                    <Icon className="h-4 w-4 mr-2 text-gray-400" />
                                    {item.label}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Live SAST clock */}
              <SastClock className="hidden md:flex" />

              {/* Offline Indicator */}
              <OfflineIndicator />
              
              {/* User Info & Logout - desktop only */}
              <div className="hidden lg:flex items-center gap-1 ml-1 pl-2 border-l">
                <div className="flex items-center gap-1 text-xs text-gray-600">
                  <User className="h-3 w-3" />
                  <span className="font-medium">{user?.display_name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-3 w-3 mr-1" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
          
          {/* Second Row - Nav Items (collapsible). Hidden by default on the
              till screen to give the cashier more vertical room; toggle via
              the ⌃ Tools button in the primary nav. */}
          {secondaryNavOpen && (
            <div className="hidden lg:block py-1 border-t border-gray-50">
              <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
                {secondaryNavItems.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
                {/* Daily Sales Target + Retail Outlook Indicator — placed at
                    the end of the wrapping flex so when Tools items overflow
                    onto a new row they sit alongside these widgets instead of
                    being stranded with a sea of empty space. */}
                <div className="ml-auto flex items-center gap-2">
                  <DailySalesTarget />
                  <MonthlySalesTarget />
                  <OutlookIndicator />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-12 bg-white z-50 overflow-y-auto">
            <div className="p-4 space-y-4">
              {/* Primary Navigation */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Main</h3>
                <div className="grid grid-cols-3 gap-2">
                  {primaryNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex flex-col items-center p-3 rounded-lg text-xs font-medium ${
                          isActive ? "bg-purple-100 text-purple-700" : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        <Icon className="h-5 w-5 mb-1" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Secondary Navigation */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Tools</h3>
                <div className="grid grid-cols-3 gap-2">
                  {secondaryNavItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex flex-col items-center p-3 rounded-lg text-xs font-medium ${
                          isActive ? "bg-purple-100 text-purple-700" : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        <Icon className="h-5 w-5 mb-1" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* Management Groups — collapsible like the desktop dropdown */}
              {managementGroups.map((group) => {
                const GroupIcon = group.groupIcon || Briefcase;
                const activeInGroup = group.items.some(it => location.pathname === it.path);
                const isExpanded = expandedGroup === group.title || activeInGroup;
                return (
                  <div key={group.title} className="border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.title)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm font-semibold ${
                        activeInGroup ? "bg-purple-50 text-purple-700" : "bg-gray-50 text-gray-700"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <GroupIcon className="h-4 w-4" />
                        {group.title}
                        <span className="text-[10px] font-normal text-gray-400">({group.items.length})</span>
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                    {isExpanded && (
                      <div className="grid grid-cols-3 gap-2 p-2">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`flex flex-col items-center p-3 rounded-lg text-xs font-medium ${
                                isActive ? "bg-purple-100 text-purple-700" : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              <Icon className="h-5 w-5 mb-1" />
                              {item.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Logout */}
              <div className="pt-4 border-t">
                <Button
                  onClick={logout}
                  variant="outline"
                  className="w-full text-red-600 border-red-200 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
      <main className="max-w-full mx-auto">
        {children}
      </main>
      
      {/* Manager Alert Listener - Only active for admin users */}
      <ManagerAlertListener isAdmin={isAdmin} />
    </div>
  );
};

export default Layout;
